// src/components/LeftNav.js
import React, { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../utils/AuthContext';
import { useCommunityNotifications } from '../utils/CommunityNotificationContext';
import { useNewsNotifications } from '../utils/NewsNotificationContext';
import { useAdminNotifications } from '../utils/AdminNotificationContext';
import {
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Divider, Typography, Box, Button, AppBar, Toolbar, IconButton,
  useMediaQuery, useTheme, Badge
} from '@mui/material';
import {
  Leaderboard as LeaderboardIcon, Person as PersonIcon,
  AdminPanelSettings as AdminIcon, Login as LoginIcon, Logout as LogoutIcon,
  Menu as MenuIcon, Launch as LaunchIcon, History as HistoryIcon,
  Group as GroupIcon, Info as InfoIcon,
  CalendarMonth as CalendarIcon,
  Newspaper as NewsIcon,
  Gavel as RulesIcon
} from '@mui/icons-material';

const drawerWidth = 220;

function LeftNav() {
  const { user, logout } = useContext(AuthContext);
  const { hasNewPosts } = useCommunityNotifications();
  const { pendingLapsCount } = useAdminNotifications();
  const { hasNewNews } = useNewsNotifications();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const menuItems = [
    { text: 'Leaderboard', icon: <LeaderboardIcon />, path: '/' },
    { text: 'News', icon: <NewsIcon />, path: '/news' },
    { text: 'Calendar', icon: <CalendarIcon />, path: '/calendar' },
    { text: 'Historical Results', icon: <HistoryIcon />, path: '/historical-results' },
    { text: 'Community', icon: <GroupIcon />, path: '/community' },
    { text: 'Rules', icon: <RulesIcon />, path: '/rules' },
    { text: 'About', icon: <InfoIcon />, path: '/about' },
  ];

  if (user) {
    menuItems.push({ text: 'Profile', icon: <PersonIcon />, path: '/profile' });
    if (user.role === 'admin') {
      menuItems.push({ text: 'Admin', icon: <AdminIcon />, path: '/admin' });
    }

  }

  const externalItems = [
    { text: 'Hot Setup', logo: '/HotSetupLogo.png', url: 'https://hotsetup.app' },
    { text: 'Mountain Pass Performance', logo: '/MPPLogo.png', url: 'https://www.MountainPassPerformance.com' },
  ];

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const drawer = (
    <>
      {/* Logo */}
      <Box sx={{ p: '18px 16px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
        <img src="/M3CLogo.png" alt="Model 3 Challenge" style={{ height: 36, width: 'auto' }} />
        <Typography className="series-year">2026</Typography>

        {/* Presented by MPP */}
        <div style={{
          marginTop: 10,
          paddingTop: 10,
          borderTop: '1px solid var(--border)',
          width: '100%',
          textAlign: 'center',
        }}>
          <div style={{
            fontSize: '0.6rem',
            fontWeight: 600,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--text-tertiary)',
            marginBottom: 6,
          }}>
            Presented by
          </div>
          <a href="https://www.MountainPassPerformance.com" target="_blank" rel="noopener noreferrer"
            style={{ display: 'inline-block', textDecoration: 'none' }}>
            <img
              src="/MPPLogo.png"
              alt="Mountain Pass Performance"
              style={{ height: 28, width: 'auto', display: 'block', margin: '0 auto' }}
              onError={e => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextSibling.style.display = 'block';
              }}
            />
            <span style={{
              display: 'none',
              fontSize: '0.68rem',
              fontWeight: 700,
              color: 'var(--accent)',
              letterSpacing: '0.04em',
              lineHeight: 1.3,
            }}>
              Mountain Pass<br />Performance
            </span>
          </a>
        </div>
      </Box>

      {/* Nav items */}
      <List sx={{ flexGrow: 1, pt: '6px', px: '4px' }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              onClick={isMobile ? handleDrawerToggle : undefined}
              sx={{
                borderRadius: '10px',
                mx: '4px',
                mb: '1px',
                py: '9px',
                px: '12px',
              }}
            >
              <ListItemIcon sx={{ minWidth: 34 }}>
                {(item.text === 'Community' && hasNewPosts) || (item.text === 'Admin' && pendingLapsCount > 0) || (item.text === 'News' && hasNewNews) ? (
                  <Badge variant="dot" color="error">{item.icon}</Badge>
                ) : item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, fontFamily: 'var(--font-body)' }} />
            </ListItemButton>
          </ListItem>
        ))}

        <Divider sx={{ my: 1, mx: '8px', opacity: 0.5 }} />

        {externalItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => { window.open(item.url, '_blank'); if (isMobile) handleDrawerToggle(); }}
              sx={{ borderRadius: '10px', mx: '4px', mb: '1px', py: '8px', px: '12px' }}
            >
              <ListItemIcon sx={{ minWidth: 34 }}>
                <img
                  src={item.logo}
                  alt={item.text}
                  style={{ height: 20, width: 'auto', maxWidth: 28, objectFit: 'contain', display: 'block' }}
                  onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                />
                <span style={{ display: 'none', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)' }}>
                  <LaunchIcon style={{ fontSize: 16 }} />
                </span>
              </ListItemIcon>
              <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 500, fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Footer */}
      <Divider sx={{ opacity: 0.5 }} />
      <Box sx={{ p: '12px 14px' }}>
        {user ? (
          <>
            <Typography sx={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', mb: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-body)' }}>
              {user.displayName}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={() => { logout(); if (isMobile) handleDrawerToggle(); }}
              fullWidth
              size="small"
              sx={{ textTransform: 'none', fontFamily: 'var(--font-body)', borderRadius: '8px', fontSize: '0.82rem' }}
            >
              Logout
            </Button>
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Button variant="outlined" startIcon={<LoginIcon />} component={Link} to="/login"
              onClick={isMobile ? handleDrawerToggle : undefined} fullWidth size="small"
              sx={{ textTransform: 'none', fontFamily: 'var(--font-body)', borderRadius: '8px', fontSize: '0.82rem' }}>
              Login
            </Button>
            <Button variant="contained" component={Link} to="/signup"
              onClick={isMobile ? handleDrawerToggle : undefined} fullWidth size="small"
              sx={{ textTransform: 'none', fontFamily: 'var(--font-body)', borderRadius: '8px', fontSize: '0.82rem' }}>
              Sign Up
            </Button>
          </Box>
        )}
      </Box>
    </>
  );

  return (
    <>
      {isMobile && (
        <AppBar position="fixed" sx={{ width: { md: `calc(100% - ${drawerWidth}px)` }, ml: { md: `${drawerWidth}px` } }}>
          <Toolbar>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <img src="/M3CLogo.png" alt="Model 3 Challenge" style={{ height: 36 }} />
            </Box>
            <IconButton aria-label="open drawer" onClick={handleDrawerToggle} sx={{ display: { md: 'none' }, color: 'var(--text-primary)' }}>
              {(hasNewPosts || pendingLapsCount > 0) ? (
                <Badge variant="dot" color="error"><MenuIcon /></Badge>
              ) : <MenuIcon />}
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--surface)',
              borderRight: '1px solid var(--border)',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
    </>
  );
}

export default LeftNav;
